import React from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Navigation, Loader2, X } from 'lucide-react';

/**
 * Reusable Location Controls Component
 * Provides consistent location-based filtering UI across listing pages
 */
export const LocationControls = ({
  userLocation,
  locationLoading,
  onRequestLocation,
  onClearLocation,
  maxDistance,
  onMaxDistanceChange,
  sortBy,
  onSortByChange,
  showSortOptions = true,
  className = ''
}) => {
  return (
    <div className={`flex flex-wrap items-center gap-3 ${className}`}>
      {/* Location Status Badge */}
      {userLocation && (
        <Badge variant="secondary" className="flex items-center gap-1">
          <Navigation className="w-3 h-3" />
          Using your location
          <button 
            onClick={onClearLocation}
            className="ml-1 hover:text-destructive"
            title="Clear location"
          >
            <X className="w-3 h-3" />
          </button>
        </Badge>
      )}
      
      {/* Request/Update Location Button */}
      <Button 
        variant="outline" 
        onClick={onRequestLocation}
        disabled={locationLoading}
        className="flex items-center gap-2"
        data-testid="use-location-btn"
      >
        {locationLoading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Navigation className="w-4 h-4" />
        )}
        {userLocation ? 'Update Location' : 'Use My Location'}
      </Button>
      
      {/* Distance Filter (only show when location is available) */}
      {userLocation && (
        <>
          <Select value={maxDistance} onValueChange={onMaxDistanceChange}>
            <SelectTrigger className="w-40" data-testid="max-distance-select">
              <SelectValue placeholder="Max Distance" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any Distance</SelectItem>
              <SelectItem value="5">Within 5 miles</SelectItem>
              <SelectItem value="10">Within 10 miles</SelectItem>
              <SelectItem value="25">Within 25 miles</SelectItem>
              <SelectItem value="50">Within 50 miles</SelectItem>
              <SelectItem value="100">Within 100 miles</SelectItem>
            </SelectContent>
          </Select>
          
          {/* Sort Options */}
          {showSortOptions && (
            <Select value={sortBy} onValueChange={onSortByChange}>
              <SelectTrigger className="w-36" data-testid="sort-by-select">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="distance">Nearest First</SelectItem>
                <SelectItem value="rating">Highest Rated</SelectItem>
                <SelectItem value="name">Name (A-Z)</SelectItem>
              </SelectContent>
            </Select>
          )}
        </>
      )}
    </div>
  );
};

/**
 * Distance display badge component
 */
export const DistanceBadge = ({ distance, className = '' }) => {
  if (!distance && distance !== 0) return null;
  
  return (
    <Badge variant="outline" className={`flex items-center gap-1 ${className}`}>
      <Navigation className="w-3 h-3" />
      {distance < 1 ? `${(distance * 5280).toFixed(0)} ft` : `${distance.toFixed(1)} mi`}
    </Badge>
  );
};

export default LocationControls;
